import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { usePaymentsModalButton, PaymentRequestModal } from '@frontend/payments-collection-flow';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { theme } from '@frontend/theme';
import { Button, MultiActionButton, PlusIcon, useModalControl } from '@frontend/design-system';
import { useNavigateWithAlert } from '../../../hooks';
import { PaymentsNavAlerts } from '../../../utils';
import { BulkPaymentRequestModal } from '../../Collections/BulkRequestModal';

const styles = {
  createButton: css`
    width: auto;
    padding-left: ${theme.spacing(1)};
  `,
  plusIcon: css`
    margin-right: ${theme.spacing(1)};
  `,
};

export type NewInvoiceActionProps = {
  trackingId?: string;
  showCollectionButton?: boolean;
};

export const NewInvoiceAction = ({ trackingId, showCollectionButton }: NewInvoiceActionProps) => {
  const { t } = useTranslation('payments');
  const { modalProps: collectionModalProps, triggerProps: collectionTriggerProps } = useModalControl();
  const { navigate } = useNavigateWithAlert({ key: PaymentsNavAlerts.CreatePaymentRequest, type: 'success' });

  const { paymentRequestModalProps, triggerProps } = usePaymentsModalButton();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });

  return (
    <>
      {showCollectionButton ? (
        <MultiActionButton
          css={styles.createButton}
          iconButtonStyleOverrides={{
            borderLeft: `1px solid ${theme.colors.primary30}`,
            paddingLeft: theme.spacing(1),
            paddingRight: theme.spacing(1.5),
            width: 'auto',
          }}
          actions={[
            {
              label: t('Create Requests For All'),
              trackingId: `${trackingId}-all`,
              ...collectionTriggerProps,
            },
          ]}
          label={
            <>
              <PlusIcon />
              {isMobile ? t('New') : t('Request Payment')}
            </>
          }
          menuPlacement='bottom-end'
          trackingId={trackingId}
          {...triggerProps}
        />
      ) : (
        <Button
          trackingId={trackingId}
          iconName='plus'
          variant='primary'
          size='large'
          css={styles.createButton}
          {...triggerProps}
        >
          {isMobile ? t('New') : t('Request Payment')}
        </Button>
      )}

      <PaymentRequestModal
        {...paymentRequestModalProps}
        redirectToInvoiceDetailPage={(invoice) => {
          navigate(`/payments/invoices/${invoice.id}`);
        }}
      />
      <BulkPaymentRequestModal modalProps={collectionModalProps} />
    </>
  );
};
